/**
 * App-wide offline wiring (Phase 16C): repositories, outbox, local actions and the sync engine,
 * bound to the OfflineSession. The engine runs while the store is unlocked and stops on lock.
 */
import { apiClient, onServerTime } from '../api-client';
import type { SealedSyncRequestDto, SyncRequestDto, SyncResponseDto } from '../../dtos/offline/sync.dto';
import { OfflineSession } from './offlineSession';
import { sessionRuntime } from './runtime';
import { VisitRepo } from './repos/visitRepo';
import { ChartRepo } from './repos/chartRepo';
import { AssessmentRepo } from './repos/assessmentRepo';
import { BlobRepo } from './repos/blobRepo';
import { Outbox } from './outbox';
import { LocalActions } from './localActions';
import { MAX_BATCH_OPS, SyncEngine, SyncTransport, WipeReason } from './syncEngine';
import { DeviceIds, DeviceService } from './deviceService';
import type { SealedOpDto } from '../../dtos/offline/sync.dto';
import { DeviceContext } from './deviceContext';
import { OfflineMode } from './offlineMode';

export const SYNC_ENDPOINT = '/patientmr/sync';
/** window event: this user's offline data was erased after a revoke or downgrade ({ detail: { reason } }) */
export const OFFLINE_WIPED_EVENT = 'offline-data-wiped';

const rt = sessionRuntime(OfflineSession);
const visits = new VisitRepo(rt);
const charts = new ChartRepo(rt);
const assessments = new AssessmentRepo(rt);
const blobs = new BlobRepo(rt);
const outbox = new Outbox(rt, blobs);
const local = new LocalActions({ rt, visits, charts, assessments, blobs, outbox });

export const syncTransport: SyncTransport = {
  async sync(body: SyncRequestDto | SealedSyncRequestDto, headers: Record<string, string>): Promise<SyncResponseDto> {
    const response = await apiClient.post<SyncResponseDto>(SYNC_ENDPOINT, body, { headers });
    return response.data || { results: [] };
  },
  heartbeat: (input) => DeviceService.heartbeat(input.deviceId, input.pendingOps, input.oldestPendingAt, input.pendingVisitIds, input.pendingShiftIds),
};

const engine = new SyncEngine({
  rt,
  outbox,
  visits,
  charts,
  assessments,
  blobs,
  transport: syncTransport,
  onWipe: async (reason: WipeReason) => {
    await OfflineSession.wipe().catch(() => undefined);
    if (reason === 'downgrade') OfflineMode.setPlanDisabled(true);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(OFFLINE_WIPED_EVENT, { detail: { reason } }));
  },
  onSettings: (settings) => { OfflineSession.markOnlineAuth(undefined, settings).catch(() => undefined); },
});

export const Offline = { rt, visits, charts, assessments, blobs, outbox, local, engine };

let started = false;
let lastAnchorWrite = 0;

/** Call once at app start. */
export function initOfflineRuntime(): void {
  if (started) return;
  started = true;
  OfflineMode.bind({ unlocked: () => Boolean(OfflineSession.getStore() && OfflineSession.getProfile()), outbox: () => (OfflineSession.getStore() ? outbox : null) });

  const onSession = () => {
    const unlocked = Boolean(OfflineSession.getStore() && OfflineSession.getProfile());
    DeviceContext.setEnrolledDeviceId(unlocked ? OfflineSession.getProfile()?.deviceId || null : null);
    if (unlocked && DeviceContext.isMobile()) engine.start();
    else engine.stop();
  };
  OfflineSession.subscribe(onSession);
  onSession();

  // Time anchor (design §6.6): last server Date vs device clock, at most once a minute
  // (guarded: some test suites mock apiClient without it)
  if (typeof onServerTime === 'function') onServerTime((serverAt, deviceAt) => {
    if (!OfflineSession.getStore() || Date.now() - lastAnchorWrite < 60_000) return;
    lastAnchorWrite = Date.now();
    outbox.setTimeAnchor({ serverAt, deviceAt }).catch(() => undefined);
  });
}

/**
 * After an online sign-in on a phone: upload sealed copies left by a PIN shred (design §6.4,
 * E2E-15 step 9), then delete that database. Returns the number of ops handed over.
 */
export async function uploadOrphanEscrow(tenantId: string, userId: string): Promise<number> {
  if (!DeviceContext.isMobile()) return 0;
  const orphan = await OfflineSession.orphanEscrow(tenantId, userId).catch(() => null);
  if (!orphan) return 0;
  const rows = (await orphan.store.getAllSealed()) as Array<{ key: string; value: SealedOpDto }>;
  const deviceId = DeviceIds.get(orphan.identityId);
  let uploaded = 0;
  for (let i = 0; i < rows.length; i += MAX_BATCH_OPS) {
    const chunk = rows.slice(i, i + MAX_BATCH_OPS).map((r) => r.value);
    if (!deviceId) return uploaded; // the body needs this phone's device id
    const response = await syncTransport.sync({ deviceId, sealed: true, ops: chunk }, { 'X-Device-Class': 'MOBILE', 'X-Pending-Ops': String(rows.length - uploaded) });
    for (const r of response.results || []) {
      // Anything else (e.g. ESCROW_KEY_UNKNOWN) stays sealed on the device for support to recover
      if (r.status === 'applied' || r.status === 'duplicate' || r.quarantined || r.status === 'quarantined') {
        await orphan.store.deleteSealed(r.opId);
        uploaded += 1;
      }
    }
  }
  if (!(await orphan.store.hasRows('escrow'))) await orphan.store.destroy();
  return uploaded;
}

/** Unsynced op count (0 when locked or not enrolled). */
export async function pendingOpCount(): Promise<number> {
  if (!OfflineSession.getStore()) return 0;
  try {
    return (await outbox.counts()).pending;
  } catch {
    return 0;
  }
}

/** A visit is active locally (service worker update deferral, logout warning). */
export async function hasActiveLocalVisit(): Promise<boolean> {
  if (!OfflineSession.getStore()) return false;
  try {
    const active = await visits.getActive();
    return Boolean(active && active.origin === 'local');
  } catch {
    return false;
  }
}
